import { useSearchStore } from '@/stores/feed/searchStore'
import type { AvailableFiltersType } from '@/types'
import { getCarriedCharacteristics } from '@/utils/search/characteristics/carriedCharacteristics'
import { hasUniformEligibleSlottedFilter } from '@/utils/search/characteristics/slottedCharacteristics'

import { filtersStateService } from './FiltersStateService'
import { filtersTransformService } from './FiltersTransformService'
import type {
  ApplyFiltersParams,
  FacetedFilterUpdateParams,
  FacetedFilterUpdater,
  InitialSearchParams,
} from './filtersTypes'
import { filtersUrlService } from './FiltersUrlService'

export class FiltersService {
  public readonly state = filtersStateService
  public readonly url = filtersUrlService
  public readonly transform = filtersTransformService

  async handleFacetedFilterUpdate(
    params: FacetedFilterUpdateParams,
    updateFiltersHook: FacetedFilterUpdater
  ): Promise<{
    availableFilters: AvailableFiltersType
    totalElements: number
  } | null> {
    const {
      searchTerm,
      draftState,
      appliedState,
      availableFilters,
      totalElements,
    } = params

    const draftStateString = this.state.createFiltersStateString(draftState)

    const appliedStateString = this.state.createAppliedStateString(
      appliedState.withLeftovers,
      appliedState.withDiscontinued,
      appliedState.stores,
      appliedState.brands,
      appliedState.categories,
      appliedState.parents,
      appliedState.characteristics,
      appliedState.matchUniformSlottedCharacteristics
    )

    if (draftStateString === appliedStateString) {
      return {
        availableFilters,
        totalElements,
      }
    }

    const result = await updateFiltersHook({
      query: searchTerm,
      categories: draftState.selectedCategories,
      brands: draftState.selectedBrands,
      characteristicFilters: draftState.selectedCharacteristics,
      withLeftovers: draftState.searchWithLeftovers,
      stores: draftState.selectedStores,
      parents: draftState.selectedParents,
      withDiscontinued: draftState.searchWithDiscontinued,
      matchUniformSlottedCharacteristics: draftState.searchMatchUniformSlotted,
    })

    if (!result) return null

    return {
      availableFilters: result.availableFilters,
      totalElements: result.totalElements,
    }
  }

  applyFilters(params: ApplyFiltersParams): void {
    const {
      isNomenclatureEditor,
      searchTerm,
      selectedCategories,
      selectedBrands,
      selectedStores,
      selectedCharacteristics,
      searchWithLeftovers,
      searchWithDiscontinued,
      searchMatchUniformSlottedCharacteristics,
      selectedParents,
      currentlySearchableNomenclatureGroupId,
      setters,
      searchHook,
      nomenclatureEditorSearchHook,
    } = params

    const currentProductCodes =
      useSearchStore.getState().currentlySearchableProductCodes

    const effectiveMatchUniformSlottedCharacteristics =
      (searchMatchUniformSlottedCharacteristics ??
        useSearchStore.getState().matchUniformSlottedCharacteristics) &&
      hasUniformEligibleSlottedFilter(selectedCharacteristics)

    if (isNomenclatureEditor) {
      setters.setNomenclatureEditorProductsLoading(true)
      nomenclatureEditorSearchHook({
        query: searchTerm,
        categories: selectedCategories,
        nomenclatureGroupId: currentlySearchableNomenclatureGroupId ?? '',
        brands: selectedBrands,
        parents: selectedParents,
        page: 0,
        characteristicFilters: selectedCharacteristics,
        withLeftovers: searchWithLeftovers,
        withDiscontinued: searchWithDiscontinued,
        matchUniformSlottedCharacteristics:
          effectiveMatchUniformSlottedCharacteristics,
      })
    } else {
      setters.setProductsLoading(true)
      setters.setCurrentlySearchableBrands(selectedBrands)
      setters.setCurrentlySearchableCategories(selectedCategories)
      setters.setCurrentlySearchableStores(selectedStores)
      setters.setWithLeftovers(searchWithLeftovers)
      setters.setCurrentlySearchableCharacteristics(selectedCharacteristics)

      if (setters.setCurrentlySearchableParents) {
        setters.setCurrentlySearchableParents(selectedParents || [])
      }
      if (setters.setWithDiscontinued) {
        setters.setWithDiscontinued(searchWithDiscontinued ?? true)
      }
      if (setters.setMatchUniformSlottedCharacteristics) {
        setters.setMatchUniformSlottedCharacteristics(
          effectiveMatchUniformSlottedCharacteristics
        )
      }

      searchHook({
        query: searchTerm,
        categories: selectedCategories,
        brands: selectedBrands,
        page: 0,
        productCodes: currentProductCodes,
        characteristicFilters: selectedCharacteristics,
        withLeftovers: searchWithLeftovers,
        stores: selectedStores,
        parents: selectedParents || [],
        withDiscontinued: searchWithDiscontinued ?? true,
        matchUniformSlottedCharacteristics:
          effectiveMatchUniformSlottedCharacteristics,
      })
    }
  }

  handleInitialSearch(params: InitialSearchParams): void {
    const { searchParams, setters, searchHook, navigate } = params

    try {
      const rawParsedParams = this.url.parseSearchParams(searchParams)
      const hasParams = this.url.hasSearchParams(rawParsedParams)

      const existingInitial = useSearchStore.getState().initialFilters

      if (hasParams) {
        const filters = this.url.parseAllFiltersSync(searchParams)

        const shouldSetBaseline = !existingInitial

        setters.setSearchTerm(filters.query)
        setters.setWithLeftovers(filters.withLeftovers)
        setters.setCurrentlySearchableCategories(filters.categories)
        setters.setCurrentlySearchableBrands(filters.brands)
        setters.setCurrentlySearchableStores(filters.stores)
        setters.setCurrentlySearchableCharacteristics(filters.characteristics)

        if (setters.setCurrentlySearchableParents) {
          setters.setCurrentlySearchableParents(filters.parents)
        }
        if (setters.setWithDiscontinued) {
          setters.setWithDiscontinued(filters.withDiscontinued)
        }
        if (setters.setMatchUniformSlottedCharacteristics) {
          setters.setMatchUniformSlottedCharacteristics(
            filters.matchUniformSlottedCharacteristics
          )
        }

        setters.setProductsLoading(true)
        setters.setPaginatedTableLoading(true)
        setters.setExpandedRows([])

        searchHook({
          query: filters.query,
          productCodes: filters.productCodes ?? [],
          categories: filters.categories,
          brands: filters.brands,
          page: filters.page,
          characteristicFilters: filters.characteristics,
          withLeftovers: filters.withLeftovers,
          stores: filters.stores,
          parents: filters.parents,
          withDiscontinued: filters.withDiscontinued,
          matchUniformSlottedCharacteristics:
            filters.matchUniformSlottedCharacteristics,
          triggerNavigation: false,
          isInitial: shouldSetBaseline,
        })
      } else {
        const shouldSetBaseline = !existingInitial

        setters.setProductsLoading(true)
        setters.setPaginatedTableLoading(true)
        setters.setExpandedRows([])

        setters.setSearchTerm('')
        setters.setWithLeftovers(false)
        setters.setCurrentlySearchableCategories([])
        setters.setCurrentlySearchableBrands([])
        setters.setCurrentlySearchableStores([])
        setters.setCurrentlySearchableCharacteristics([])

        if (setters.setCurrentlySearchableParents)
          setters.setCurrentlySearchableParents([])
        if (setters.setWithDiscontinued) setters.setWithDiscontinued(true)
        if (setters.setMatchUniformSlottedCharacteristics) {
          setters.setMatchUniformSlottedCharacteristics(false)
        }

        setters.setServerChosenFilters(null)

        searchHook({
          query: '',
          productCodes: [],
          categories: [],
          brands: [],
          page: 0,
          characteristicFilters: [],
          withLeftovers: false,
          stores: [],
          parents: [],
          withDiscontinued: true,
          triggerNavigation: false,
          isInitial: shouldSetBaseline,
        })
      }
    } catch (err) {
      console.error('Критическая ошибка при синхронизации фильтров URL:', err)
      navigate('/')
    }
  }

  resetAllSearchFilters(
    options: { keepCarriedCharacteristics?: boolean } = {}
  ): void {
    const searchStore = useSearchStore.getState()
    const keptCharacteristics = options.keepCarriedCharacteristics
      ? getCarriedCharacteristics()
      : []
    searchStore.setInitialFilters(null)
    searchStore.setSearchTerm('')
    searchStore.setCurrentlySearchableBrands([])
    searchStore.setCurrentlySearchableCategories([])
    searchStore.setCurrentlySearchableCharacteristics(keptCharacteristics)
    searchStore.setCurrentlySearchableStores([])
    searchStore.setCurrentlySearchableParents([])
    searchStore.setWithLeftovers(false)
    searchStore.setWithDiscontinued(true)
    if (keptCharacteristics.length === 0) {
      searchStore.setMatchUniformSlottedCharacteristics(false)
    }
    searchStore.setServerChosenFilters(null)
  }
}

export const filtersService = new FiltersService()
